import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { uploadApi } from '../api/upload.api';
import Logo from '../components/Logo';
import VoiceRecorder from '../components/VoiceRecorder';
import CreatePromptConfirmModal from '../components/CreatePromptConfirmModal';
import PhotoSourceChooser, { isCameraCaptureSupported } from '../components/PhotoSourceChooser';
import ThemeToggle from '../components/ThemeToggle';
import UserMenu from '../components/UserMenu';
import usePageMeta from '../hooks/usePageMeta';

// Kept to plain-text-readable formats — anything else (PDF, DOCX, images)
// would need server-side parsing, which is out of scope here.
const ACCEPTED_PROMPT_FILE_TYPES = '.txt,.md,text/plain,text/markdown';
const MAX_PROMPT_FILE_SIZE_BYTES = 1024 * 1024; // 1MB is more than enough for a text prompt

const MAX_REFERENCE_IMAGES = 8;
const MAX_REFERENCE_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // matches the server's generic upload limit

// Mirrors the server's upload allowlist (see upload.router.ts). Narrower than
// `image/*` on purpose: HEIC (an iPhone's native format), SVG and TIFF can't
// be read by the vision models or rendered by every browser, so they'd end up
// as broken images on the finished website. Listing the types here means the
// OS file picker greys them out instead of the upload failing afterwards.
const ACCEPTED_REFERENCE_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPTED_REFERENCE_IMAGE_MIMETYPES = ACCEPTED_REFERENCE_IMAGE_TYPES.split(',');

interface ReferenceImage {
  url: string;
  previewUrl: string;
}

const CreateWebsitePage: React.FC = () => {
  usePageMeta({ title: 'Create a website — OnTheWeb', index: false });

  const location = useLocation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<string>(location.state?.prompt || '');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Separate input purely so it can carry `capture`, which is what asks the OS
  // for the camera rather than the photo library.
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Reference photos (shop front, products, menu, etc.) the business can attach so the
  // AI can pull real business/product/gallery details from them, not just the text prompt.
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [photoSourceOpen, setPhotoSourceOpen] = useState(false);

  const createWebsiteMutation = useMutation({
    mutationFn: websiteApi.createWebsiteFromPrompt,
    onSuccess: (data) => {
      navigate('/dashboard', {
        state: {
          message: "We're building your website! Track its progress below — it usually takes around 5 minutes.",
          websiteId: data._id,
        },
      });
    },
    onError: (err: any) => {
      console.error('Error creating website:', err);
      setError(err.response?.data?.message || 'Failed to create website. Please try again.');
    },
  });

  // The AI knows nothing about the business beyond this description, and the run
  // takes minutes, so it gets one last read before generation starts.
  const handleReviewPrompt = () => {
    if (!prompt.trim()) {
      setError('Please describe your business to get started');
      return;
    }
    setError('');
    setConfirmOpen(true);
  };

  const handleEditPrompt = () => {
    setConfirmOpen(false);
    promptRef.current?.focus();
  };

  const handleCreateWebsite = () => {
    setConfirmOpen(false);
    createWebsiteMutation.mutate({
      prompt,
      referenceImageUrls: referenceImages.length > 0 ? referenceImages.map((img) => img.url) : undefined,
    });
  };

  /**
   * One button, then a choice of source — rather than a separate button per
   * source. Where there is no camera to offer there is only one real option, so
   * the chooser is skipped and the file picker opens directly.
   */
  const handleAddPhotosClick = () => {
    if (isCameraCaptureSupported) {
      setPhotoSourceOpen(true);
    } else {
      imageInputRef.current?.click();
    }
  };

  /**
   * Validates and uploads reference photos, whichever way they arrived — picked
   * from the device or taken with the camera. Both routes end up here so there
   * is one place where the limits live.
   */
  const uploadReferenceImages = async (files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_REFERENCE_IMAGES - referenceImages.length;
    if (remainingSlots <= 0) {
      setError(`You can attach up to ${MAX_REFERENCE_IMAGES} photos.`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    const oversizedFile = filesToUpload.find((f) => f.size > MAX_REFERENCE_IMAGE_SIZE_BYTES);
    if (oversizedFile) {
      setError('Please select image files under 10MB each.');
      return;
    }
    const unsupportedFile = filesToUpload.find((f) => !ACCEPTED_REFERENCE_IMAGE_MIMETYPES.includes(f.type.toLowerCase()));
    if (unsupportedFile) {
      setError(
        'Please use JPG, PNG, WebP or GIF photos. On iPhone, pick the photo from your Photos library rather than the Files app.'
      );
      return;
    }

    setError('');
    setUploadingImages(true);
    try {
      const urls = await uploadApi.uploadFiles(filesToUpload, 'prompt-reference-images');
      const newImages = urls.map((url, i) => ({ url, previewUrl: URL.createObjectURL(filesToUpload[i]) }));
      setReferenceImages((prev) => [...prev, ...newImages]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload one or more photos. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImagesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await uploadReferenceImages(files);
  };

  const handleRemoveReferenceImage = (url: string) => {
    setReferenceImages((prev) => prev.filter((img) => img.url !== url));
  };

  const handleTranscribed = (text: string) => {
    setError('');
    setPrompt((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_PROMPT_FILE_SIZE_BYTES) {
      setError('File is too large. Please upload a text file under 1MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result.trim() : '';
      if (!text) {
        setError('Could not read any text from that file. Please try a plain text (.txt or .md) file.');
        return;
      }
      setError('');
      setFileName(file.name);
      setPrompt(text);
    };
    reader.onerror = () => {
      setError('Failed to read the file. Please try again.');
    };
    reader.readAsText(file);
  };

  const isCreating = createWebsiteMutation.isPending;

  return (
    <div className="nova-page">
      <nav className="nova-nav relative border-b border-nova-border">
        <div className="nova-nav-inner">
          <Logo />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={() => navigate('/dashboard')} className="btn-ghost">Cancel</button>
            <UserMenu />
          </div>
        </div>
      </nav>

      {/* `overflow-hidden` clips the decorative glow below, which is a fixed
          600px wide. Narrower than that and it reaches past the right edge,
          making the whole page scroll sideways. The landing and login pages
          clip it on their containers for the same reason. */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 pt-24 sm:pt-28 relative overflow-hidden">
        <div className="hero-glow top-0 left-1/2 -translate-x-1/2 opacity-50" />

        {isCreating ? (
          <div className="text-center py-20 relative z-10">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-nova-sky mb-8"></div>
            <h2 className="font-display text-3xl font-extrabold text-nova-text mb-4">Starting Website Creation...</h2>
            <p className="text-xl text-nova-text2 mb-2">
              Our AI is analyzing your business and planning your sections.
            </p>
            <p className="text-sm text-nova-text3">
              You'll be redirected to your dashboard in a few seconds, where you can watch live progress.
            </p>
          </div>
        ) : (
          <div className="text-center relative z-10">
            <div className="section-tag">New Website</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-nova-text mb-4">Create Your Website</h2>
            <p className="text-xl text-nova-text2 mb-10">
              Describe your business and we'll create a beautiful website for you in seconds
            </p>

            <div className="nova-card p-4 sm:p-8 space-y-6 text-left shadow-nova-lg">
              {error && <div className="nova-alert-error">{error}</div>}

              <div className="space-y-3">
                <label htmlFor="prompt" className="nova-label">Tell us about your business</label>
                <VoiceRecorder onTranscribed={handleTranscribed} disabled={isCreating} />
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={isCreating}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-nova-sky/10 text-nova-sky border border-nova-sky/30 hover:bg-nova-sky/20 disabled:opacity-50 disabled:cursor-not-allowed self-start"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload a file
                  </button>
                  <span className="text-xs text-nova-text3">Or upload a .txt/.md file with your business details</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_PROMPT_FILE_TYPES}
                    onChange={handleFileSelected}
                    className="hidden"
                  />
                </div>
                {fileName && (
                  <p className="text-sm text-nova-text3">
                    Loaded from <span className="font-medium text-nova-text2">{fileName}</span>. Feel free to edit the text below before creating your website.
                  </p>
                )}

                <div className="space-y-2 pt-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={handleAddPhotosClick}
                      disabled={isCreating || uploadingImages || referenceImages.length >= MAX_REFERENCE_IMAGES}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-nova-sky/10 text-nova-sky border border-nova-sky/30 hover:bg-nova-sky/20 disabled:opacity-50 disabled:cursor-not-allowed self-start"
                    >
                      {uploadingImages ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Add photos of your shop/products
                        </>
                      )}
                    </button>

                    <span className="text-xs text-nova-text3">
                      Optional — up to {MAX_REFERENCE_IMAGES} photos. We'll use them to build your gallery and get product/service details right.
                    </span>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={ACCEPTED_REFERENCE_IMAGE_TYPES}
                      multiple
                      onChange={handleImagesSelected}
                      className="hidden"
                    />
                    {/* `capture` hands off to the phone's own camera app, which
                        avoids building a viewfinder — and the shutter, the
                        preview and the orientation handling that come with it.
                        It returns one shot at a time, so no `multiple` here. */}
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept={ACCEPTED_REFERENCE_IMAGE_TYPES}
                      capture="environment"
                      onChange={handleImagesSelected}
                      className="hidden"
                    />
                  </div>
                  {referenceImages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {referenceImages.map((img) => (
                        <div key={img.url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-nova-border flex-shrink-0">
                          <img src={img.previewUrl} alt="Reference" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => handleRemoveReferenceImage(img.url)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                            aria-label="Remove photo"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <textarea
                  id="prompt"
                  ref={promptRef}
                  rows={5}
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    if (fileName) setFileName('');
                  }}
                  className="nova-input resize-none"
                  placeholder="e.g., I run a cozy Italian restaurant in downtown. We serve authentic pasta, wood-fired pizzas, and have a great wine selection."
                  disabled={isCreating}
                />
              </div>

              <div className="flex gap-4 pt-2">
                <button type="button" onClick={() => navigate('/dashboard')} className="btn-ghost flex-1 justify-center py-3.5">
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleReviewPrompt}
                  disabled={!prompt.trim() || isCreating}
                  className="btn-hero flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create My Website
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <CreatePromptConfirmModal
        isOpen={confirmOpen}
        prompt={prompt}
        photoCount={referenceImages.length}
        onEdit={handleEditPrompt}
        onConfirm={handleCreateWebsite}
      />

      {photoSourceOpen && (
        <PhotoSourceChooser
          multiple
          onPickCamera={() => {
            setPhotoSourceOpen(false);
            cameraInputRef.current?.click();
          }}
          onPickGallery={() => {
            setPhotoSourceOpen(false);
            imageInputRef.current?.click();
          }}
          onCancel={() => setPhotoSourceOpen(false)}
        />
      )}
    </div>
  );
};

export default CreateWebsitePage;
